package ru.chm.gradereport.ui.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color

/** Accents shared by both themes. */
val GreenAccent = Color(0xFF22C55E)
val BlueAccent = Color(0xFF38BDF8)
val YellowAccent = Color(0xFFFBBF24)
val RedAccent = Color(0xFFEF5350)

data class GradeColors(
    val isDark: Boolean,
    val surface: Color,
    val card: Color,
    val border: Color,
    val textPrimary: Color,
    val textMuted: Color,
    val blueAccent: Color,
    val yellowAccent: Color,
    val tableHeader: Color,
    val tableRowEven: Color,
    val tableRowOdd: Color,
    val gradeCellBg: Color,
    val gradeCellBorder: Color,
    val nestedCard: Color,
    val fieldHover: Color,
    val fieldPress: Color,
    val scrollTrack: Color,
    val scrollThumb: Color,
    val errorBanner: Color,
)

val DarkGradeColors = GradeColors(
    isDark = true,
    surface = Color(0xFF111827),
    card = Color(0xFF1F2937),
    border = Color(0xFF374151),
    textPrimary = Color.White,
    textMuted = Color(0xFF9CA3AF),
    blueAccent = BlueAccent,
    yellowAccent = YellowAccent,
    tableHeader = Color(0xFF0B1220),
    tableRowEven = Color(0xFF162032),
    tableRowOdd = Color(0xFF1E293B),
    gradeCellBg = Color(0xFF0F172A),
    gradeCellBorder = Color(0xFF334155),
    nestedCard = Color(0xFF162032),
    fieldHover = Color(0xFF243044),
    fieldPress = Color(0xFF2A384F),
    scrollTrack = Color(0xFF374151),
    scrollThumb = Color(0xFF9CA3AF),
    errorBanner = Color(0xFF7F1D1D),
)

val LightGradeColors = GradeColors(
    isDark = false,
    surface = Color(0xFFF1F5F9),
    card = Color(0xFFFFFFFF),
    border = Color(0xFFCBD5E1),
    textPrimary = Color(0xFF0F172A),
    textMuted = Color(0xFF64748B),
    blueAccent = Color(0xFF0369A1),
    yellowAccent = Color(0xFFB45309),
    tableHeader = Color(0xFFE2E8F0),
    tableRowEven = Color(0xFFFFFFFF),
    tableRowOdd = Color(0xFFF8FAFC),
    gradeCellBg = Color(0xFFF1F5F9),
    gradeCellBorder = Color(0xFFCBD5E1),
    nestedCard = Color(0xFFF8FAFC),
    fieldHover = Color(0xFFE2E8F0),
    fieldPress = Color(0xFFCBD5E1),
    scrollTrack = Color(0xFFE2E8F0),
    scrollThumb = Color(0xFF94A3B8),
    errorBanner = Color(0xFFDC2626),
)

val LocalGradeColors = staticCompositionLocalOf { DarkGradeColors }

object GradeTheme {
    val colors: GradeColors
        @Composable
        @ReadOnlyComposable
        get() = LocalGradeColors.current
}

/** @deprecated Prefer [GradeTheme.colors]; kept for gradual migration in non-theme spots. */
val SurfaceDark: Color get() = DarkGradeColors.surface
val CardDark: Color get() = DarkGradeColors.card
val BorderDark: Color get() = DarkGradeColors.border
val TextMuted: Color get() = DarkGradeColors.textMuted

private val DarkScheme = darkColorScheme(
    primary = GreenAccent,
    onPrimary = Color.White,
    secondary = BlueAccent,
    onSecondary = Color.White,
    background = DarkGradeColors.surface,
    onBackground = DarkGradeColors.textPrimary,
    surface = DarkGradeColors.card,
    onSurface = DarkGradeColors.textPrimary,
    surfaceVariant = Color(0xFF283548),
    onSurfaceVariant = DarkGradeColors.textMuted,
    error = RedAccent,
    outline = DarkGradeColors.border,
)

private val LightScheme = lightColorScheme(
    primary = GreenAccent,
    onPrimary = Color.White,
    secondary = BlueAccent,
    onSecondary = Color.White,
    background = LightGradeColors.surface,
    onBackground = LightGradeColors.textPrimary,
    surface = LightGradeColors.card,
    onSurface = LightGradeColors.textPrimary,
    surfaceVariant = Color(0xFFE2E8F0),
    onSurfaceVariant = LightGradeColors.textMuted,
    error = RedAccent,
    outline = LightGradeColors.border,
)

@Composable
fun GradeReportTheme(
    darkTheme: Boolean = true,
    content: @Composable () -> Unit,
) {
    val colors = if (darkTheme) DarkGradeColors else LightGradeColors
    CompositionLocalProvider(LocalGradeColors provides colors) {
        MaterialTheme(
            colorScheme = if (darkTheme) DarkScheme else LightScheme,
            content = content,
        )
    }
}
